/**
 * AppGate — the only access router.
 *
 * Pure switch on (authStatus, entitlementStatus). Post-auth claim/restore/
 * verify work lives in V2Resolve, NOT here.
 *
 *   auth: loading                    -> <Loader/>
 *   auth: signed-out                 -> /v2/onboarding
 *   auth: signed-in + ent: checking  -> <Loader/>
 *   auth: signed-in + ent: active    -> children (clear stale pendingPurchase)
 *   auth: signed-in + ent: inactive  -> /v2/subscribe (no auto-launch)
 *   auth: signed-in + ent: error     -> <RetryScreen/>
 */

import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/components/AuthProvider';
import { useEntitlement } from '@/components/EntitlementProvider';
import { V2GateSkeleton } from '@/components/v2/V2Skeletons';
import { readPendingPurchase, clearPendingPurchase } from '@/lib/pending-purchase';

function RetryScreen({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex h-screen w-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
      <h2 className="text-lg font-semibold text-foreground">Couldn't verify your subscription</h2>
      <p className="max-w-xs text-sm text-muted-foreground">
        Check your connection and try again.
      </p>
      <Button onClick={onRetry} variant="default" size="lg">
        Retry
      </Button>
    </div>
  );
}

interface AppGateProps {
  children: React.ReactNode;
}

export function AppGate({ children }: AppGateProps) {
  const { user, isLoading: authLoading } = useAuth();
  const { status, refetch } = useEntitlement();
  const location = useLocation();

  // When access is active, any pending-purchase marker is stale — drop it.
  useEffect(() => {
    if (status === 'active' && readPendingPurchase()) {
      clearPendingPurchase();
    }
  }, [status]);

  useEffect(() => {
    if (!authLoading && user) {
      console.log('[AppGate]', { path: location.pathname, entitlement: status });
    }
  }, [authLoading, user, status, location.pathname]);

  // Unified loader: stay mounted across auth → entitlement → active, fade once
  // when content appears so there's only ever one visual loading surface.
  const isLoading =
    authLoading || (!!user && (status === 'checking'));
  const [showLoader, setShowLoader] = useState(isLoading);

  useEffect(() => {
    if (isLoading) {
      setShowLoader(true);
      return;
    }
    // Fade out after content mounts
    const t = setTimeout(() => setShowLoader(false), 350);
    return () => clearTimeout(t);
  }, [isLoading]);

  if (!authLoading && !user) {
    return <Navigate to="/v2/onboarding" replace state={{ from: location.pathname }} />;
  }

  if (!isLoading && status === 'error') {
    return <RetryScreen onRetry={() => { void refetch(); }} />;
  }

  if (!isLoading && status === 'inactive') {
    return <Navigate to="/v2/subscribe" replace state={{ from: location.pathname }} />;
  }

  const ready = !isLoading && status === 'active';

  return (
    <>
      {ready && children}
      {showLoader && (
        <div
          aria-hidden={!isLoading}
          className="pointer-events-none fixed inset-0 z-[9999] transition-opacity duration-300"
          style={{ opacity: isLoading ? 1 : 0 }}
        >
          <V2GateSkeleton />
        </div>
      )}
    </>
  );
}
